/**
 * ч.1442: «зберегти документ у мій журнал ЕДО» — для документа, отриманого посиланням на підпис.
 *
 * Зберегти можна в організацію (учасника ЕДО) з кодом, якого чекав відправник (або з кодом у
 * підписі), і лише після підтвердження цього коду ключем КЕП. Блок показує, чого бракує.
 * Використовується на публічній сторінці підпису і в журналі ЕДО (після реєстрації).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { detailOf, edoSign, PENDING_CLAIM_KEY } from '@/api/edoSign';

function forgetPending(token: string): void {
  try {
    if (localStorage.getItem(PENDING_CLAIM_KEY) === token) localStorage.removeItem(PENDING_CLAIM_KEY);
  } catch { /* приватне вікно */ }
}

export default function EdoClaimPanel({ token, onDone }: { token: string; onDone?: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['edo-claim', token],
    queryFn: () => edoSign.claimInfo(token),
    retry: false,
  });
  const confirmed = (q.data?.candidates ?? []).filter((c) => c.confirmed);
  const [orgId, setOrgId] = useState('');
  const target = orgId || confirmed[0]?.id || '';
  const save = useMutation({
    mutationFn: () => edoSign.claim(token, target),
    onSuccess: () => {
      forgetPending(token);
      void qc.invalidateQueries({ queryKey: ['edo-claim', token] });
      onDone?.();
    },
  });

  if (q.isLoading) return <p className="esign__note">Перевіряємо ваш обліковий запис…</p>;
  if (q.isError) {
    if ((q.error as { response?: { status?: number } })?.response?.status === 404) forgetPending(token);
    return <p className="esign__note">{detailOf(q.error, 'Не вдалося перевірити документ')}</p>;
  }
  const d = q.data!;
  if (d.saved.length > 0) {
    forgetPending(token);
    return (
      <div data-testid="edo-claim-saved">
        <p className="esign__ok" style={{ margin: '0 0 0.5rem' }}>Документ збережено у ваш журнал ЕДО.</p>
        <Link to="/edo/journal?direction=IN">відкрити журнал ЕДО</Link>
      </div>
    );
  }
  if (d.codes.length === 0) {
    return <p className="esign__note">Зберегти документ можна після підписання: тоді видно, кому він належить.</p>;
  }
  const code = d.codes.join(', ');
  if (confirmed.length === 0) {
    const pending = d.candidates[0];
    return (
      <p className="esign__note" data-testid="edo-claim-need">
        {pending
          ? <>Підтвердіть «{pending.name}» ключем КЕП з кодом {pending.code} (можна через Дія.Підпис) —
            і документ можна буде зберегти. <Link to="/organizations">перейти до учасників</Link></>
          : <>Документ адресовано коду {code}. Додайте учасника (організацію чи ФОП) з цим кодом і підтвердіть
            його ключем КЕП — і документ можна буде зберегти. <Link to="/organizations">перейти до учасників</Link></>}
      </p>
    );
  }
  return (
    <div className="esign__file" data-testid="edo-claim">
      {confirmed.length > 1 && (
        <div className="field">
          <label htmlFor="edo-claim-org">Куди зберегти</label>
          <select id="edo-claim-org" value={target} onChange={(e) => setOrgId(e.target.value)}>
            {confirmed.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.code}</option>)}
          </select>
        </div>
      )}
      <div className="esign__actions">
        <button type="button" className="primary" disabled={!target || save.isPending}
          onClick={() => save.mutate()}>
          {save.isPending ? 'зберігаємо…' : 'зберегти в мій журнал ЕДО'}
        </button>
        {confirmed.length === 1 && <span className="esign__note" style={{ margin: 0 }}>у «{confirmed[0]!.name}»</span>}
      </div>
      {save.isError && <p className="esign__error" role="alert">{detailOf(save.error, 'Не вдалося зберегти документ')}</p>}
    </div>
  );
}
